<script setup>
import { ref, computed, reactive } from 'vue';

const props = defineProps({
    lead: { type: Object, required: true },
    searchId: { type: Number, required: true },
});

const emit = defineEmits(['added', 'show-insights', 'hide']);

const adding = ref(false);
const showAddReminder = ref(false);
const reminderModalOpen = ref(false);
const savingReminder = ref(false);
const reminderError = ref('');
const reminder = reactive({
    action: '',
    date: '',
    time: '',
});

const inCrm = computed(() => props.lead.in_crm);
const crmLeadId = computed(() => props.lead.crm_id ?? props.lead.lead_id);
const reminderIsInvalid = computed(() => {
    return !reminder.action.trim() || !reminder.date || !reminder.time;
});

const domain = computed(() => {
    const d = props.lead.domain || '';
    if (!d || d === 'Domain not found') return null;
    try {
        if (d.includes('://')) return new URL(d).hostname;
        return d.replace(/^www\./, '');
    } catch {
        return d.replace(/^www\./, '');
    }
});

const logoUrl = computed(() => {
    if (!domain.value) return null;
    return `https://img.logo.dev/${domain.value}?token=pk_a]JjY0MDEtYjc3OS00NzRiLTkwYTQtNjg2NWNlMzZmMGNk&size=48&format=png`;
});

const initials = computed(() => {
    const name = props.lead.name || '';
    return name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0] || '')
        .join('')
        .toUpperCase();
});

const initialsColor = computed(() => {
    const colors = [
        'bg-purple-100 text-purple-700',
        'bg-blue-100 text-blue-700',
        'bg-green-100 text-green-700',
        'bg-amber-100 text-amber-700',
        'bg-rose-100 text-rose-700',
        'bg-cyan-100 text-cyan-700',
    ];
    const name = props.lead.name || '';
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
});

const logoError = ref(false);

const hasCloserHired = computed(() => {
    return window.myflow?.company?.hired_specialists?.includes('nina') === true;
});

const showCloserInsights = () => {
    if (!hasCloserHired.value) {
        return;
    }

    emit('show-insights', props.lead);
};

const addToCrm = async () => {
    if (adding.value || inCrm.value) return;
    adding.value = true;
    try {
        const response = await fetch(
            route('lead-searches.add-to-crm', {
                searchId: props.searchId,
                leadId: props.lead.id,
            }),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector(
                        'meta[name="csrf-token"]'
                    )?.content,
                },
            }
        );
        if (response.ok) {
            const data = await response.json();
            showAddReminder.value = true;
            emit('added', {
                leadId: props.lead.id,
                crmLeadId: data.lead_id,
                crmUrl: data.crm_url,
                email: data.email,
            });
        }
    } catch (e) {
        console.error('Failed to add to CRM', e);
    } finally {
        adding.value = false;
    }
};

const openReminderModal = () => {
    reminder.action = '';
    reminder.date = '';
    reminder.time = '';
    reminderError.value = '';
    reminderModalOpen.value = true;
};

const closeReminderModal = () => {
    if (savingReminder.value) return;
    reminderModalOpen.value = false;
    reminderError.value = '';
};

const onReminderModalClosed = () => {
    reminderModalOpen.value = false;
    reminderError.value = '';
};

const saveReminder = async () => {
    if (reminderIsInvalid.value || savingReminder.value || !crmLeadId.value)
        return;

    savingReminder.value = true;
    reminderError.value = '';

    try {
        await axios.post(route('crm.leads.action.create', crmLeadId.value), {
            action: reminder.action.trim(),
            date: reminder.date,
            time: reminder.time,
            prio: 1,
        });
        reminderModalOpen.value = false;
    } catch (error) {
        reminderError.value =
            error?.response?.data?.message ||
            _mft('lead-hunter:scout.reminderError');
    } finally {
        savingReminder.value = false;
    }
};
</script>

<template>
    <div
        class="bg-white rounded-xl border border-gray-200 p-4 flex flex-col hover:shadow-md transition-shadow"
        :class="lead.hidden ? 'opacity-50' : ''"
    >
        <!-- Header: Logo + Name -->
        <div class="flex items-start gap-3 mb-3">
            <logo-from-domain
                class="w-10 h-10"
                :email="lead.email"
                :website="lead.domain"
                :company-name="lead.name"
            />
            <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-gray-900 truncate mb-0">
                    {{ lead.name }}
                </p>
                <p class="text-xs text-gray-500 truncate mb-0">
                    {{ lead.job_title }}
                </p>
                <p class="text-[11px] text-gray-400 truncate mb-0">
                    {{ lead.company }}
                </p>
            </div>
            <span
                v-if="inCrm"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-100 text-green-700 flex-shrink-0"
            >
                {{ _mft('lead-hunter:scout.inCrm') }}
            </span>
        </div>

        <!-- Email -->
        <div class="mb-3">
            <a
                v-if="lead.email"
                :href="'mailto:' + lead.email"
                class="mf-link text-xs flex items-center gap-1.5 hover:text-brandPrimary"
            >
                <i class="fa-regular fa-envelope text-gray-400"></i>
                {{ lead.email }}
            </a>
        </div>

        <!-- Links -->
        <div class="flex items-center gap-3 mb-3">
            <a
                v-if="lead.linkedin_url"
                :href="
                    lead.linkedin_url.startsWith('http')
                        ? lead.linkedin_url
                        : 'https://' + lead.linkedin_url
                "
                target="_blank"
                class="flex items-center gap-1 text-xs text-gray-500 hover:text-brandPrimary"
            >
                <i class="fa-brands fa-linkedin text-sm"></i>
                LinkedIn
            </a>
            <container-with-tooltip
                :class="{ 'cursor-not-allowed': !hasCloserHired }"
                additional-tooltip-class="whitespace-nowrap"
                alignment="center"
                theme="dark"
            >
                <button
                    type="button"
                    :disabled="!hasCloserHired"
                    @click="showCloserInsights"
                    class="flex items-center gap-1 text-xs"
                    :class="
                        hasCloserHired
                            ? 'text-gray-500 hover:text-brandPrimary'
                            : 'text-gray-300 cursor-not-allowed'
                    "
                >
                    <i class="fa-regular fa-head-side-brain text-sm"></i>
                    {{ _mft('lead-hunter:scout.insights') }}
                </button>

                <template v-if="!hasCloserHired" #tooltip>
                    <div>
                        {{ _mft('crm:nina.requiresCloserHired') }}
                    </div>
                </template>
            </container-with-tooltip>
        </div>

        <!-- Actions -->
        <div class="mt-auto flex items-start gap-2">
            <template v-if="!inCrm">
                <button-primary
                    @click="addToCrm"
                    :disabled="adding"
                    size="sm"
                    class="flex-1 justify-center"
                >
                    <mf-spinner-medium v-if="adding" class="w-6 h-3" />
                    <template v-else>
                        <i class="fa-regular fa-plus mr-1"></i>
                        {{ _mft('lead-hunter:scout.addToCrm') }}
                    </template>
                </button-primary>
            </template>
            <template v-else>
                <a :href="lead.crm_url">
                    <button-secondary
                        size="sm"
                        class="w-full justify-center"
                    >
                        <i
                            class="fa-regular fa-arrow-up-right-from-square mr-1"
                        ></i>
                        {{ _mft('lead-hunter:scout.showInCrm') }}
                    </button-secondary>
                </a>
                <button-primary
                    v-if="showAddReminder"
                    type="button"
                    size="sm"
                    class="justify-center"
                    @click="openReminderModal"
                    v-tooltip="{ content: _mft('lead-hunter:scout.addReminder'), offset: 2 }"
                >
                    <i class="fa-regular fa-bell-plus scale-110"></i>
                </button-primary>
            </template>
            <button-transparent
                size="sm"
                @click="emit('hide', lead.id)"
                class="flex-shrink-0 text-gray-400"
                :title="
                    lead.hidden
                        ? _mft('lead-hunter:scout.unhide')
                        : _mft('lead-hunter:scout.hide')
                "
            >
                <i
                    class="fa-regular"
                    :class="lead.hidden ? 'fa-eye' : 'fa-eye-slash'"
                ></i>
            </button-transparent>
        </div>

        <modal-basic
            :open="reminderModalOpen"
            :is-dirty="
                Boolean(reminder.action || reminder.date || reminder.time)
            "
            @closed="onReminderModalClosed"
        >
            <template #header>
                {{ _mft('lead-hunter:scout.addReminder') }}
            </template>
            <template #body>
                <form @submit.prevent="saveReminder">
                    <input-textarea
                        v-model:content="reminder.action"
                        :content="reminder.action"
                        :label="_mft('lead-hunter:scout.reminderMessage')"
                        :placeholder="
                            _mft('lead-hunter:scout.reminderMessagePlaceholder')
                        "
                        input-height-class="h-24"
                        :maxlength="255"
                        required
                        name="reminder_action"
                    />

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input-date
                            v-model:content="reminder.date"
                            :content="reminder.date"
                            :label="_mft('lead-hunter:scout.reminderDate')"
                            required
                            name="reminder_date"
                        />
                        <input-time
                            v-model:content="reminder.time"
                            :content="reminder.time"
                            :label="_mft('lead-hunter:scout.reminderTime')"
                            required
                            name="reminder_time"
                        />
                    </div>

                    <p v-if="reminderError" class="text-sm text-red-600 mb-4">
                        {{ reminderError }}
                    </p>

                    <div class="flex justify-end gap-2">
                        <button-secondary
                            type="button"
                            :disabled="savingReminder"
                            @click="closeReminderModal"
                        >
                            {{ _mft('shared:action.cancel') }}
                        </button-secondary>
                        <button-primary
                            type="submit"
                            :disabled="reminderIsInvalid || savingReminder"
                        >
                            <mf-spinner-medium
                                v-if="savingReminder"
                                class="w-6 h-3"
                            />
                            <template v-else>
                                {{ _mft('shared:action.save') }}
                            </template>
                        </button-primary>
                    </div>
                </form>
            </template>
        </modal-basic>
    </div>
</template>
